import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Snackbar,
    Alert,
    Stack,
    FormGroup,
    Paper,
    Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import { API_URL, apiFetch } from "../config";

const EVENT_TYPES = [
    { value: "meeting", label: "Meeting", color: "#1E88E5" },
    { value: "call", label: "Call", color: "#00897B" },
    { value: "follow_up", label: "Follow-up", color: "#F4511E" },
    { value: "internal", label: "Internal", color: "#6A1B9A" },
    { value: "other", label: "Other", color: "#5E35B1" },
];

const typeColor = (t) => EVENT_TYPES.find((e) => e.value === t)?.color || "#3949AB";

function addDaysIso(dateStr, days) {
    const d = new Date(dateStr + (dateStr.length <= 10 ? "T12:00:00" : ""));
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

function toExclusiveEnd(inclusiveEndDate) {
    return addDaysIso(inclusiveEndDate, 1);
}

const emptyForm = {
    id: null,
    title: "",
    description: "",
    event_type: "meeting",
    all_day: true,
    start_date: "",
    end_date_inclusive: "",
    start_dt: "",
    end_dt: "",
    location: "",
    customer_id: "",
};

const Calendar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [showTasks, setShowTasks] = useState(true);
    const [showEvents, setShowEvents] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const loadEvents = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_URL}/calendar-events`);
            const data = await res.json();
            if (res.ok) setEvents(data);
            else throw new Error(data.error || "Failed to load events");
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    }, []);

    const loadTasks = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_URL}/tasks`);
            const data = await res.json();
            if (res.ok) setTasks(Array.isArray(data) ? data : []);
        } catch {
            setTasks([]);
        }
    }, []);

    const loadCustomers = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_URL}/customers`);
            const data = await res.json();
            if (res.ok) setCustomers(data);
        } catch {
            setCustomers([]);
        }
    }, []);

    useEffect(() => {
        loadEvents();
        loadTasks();
        loadCustomers();
    }, [loadEvents, loadTasks, loadCustomers]);

    const calendarApiEvents = useMemo(() => {
        const out = [];
        if (showEvents) {
            events.forEach((ev) => {
                const color = typeColor(ev.event_type);
                if (ev.all_day) {
                    const start = ev.start_at.slice(0, 10);
                    const end = ev.end_at.slice(0, 10);
                    out.push({
                        id: ev.id,
                        title: ev.title,
                        start,
                        end,
                        allDay: true,
                        backgroundColor: color,
                        borderColor: color,
                        extendedProps: { source: "calendar", row: ev },
                    });
                } else {
                    out.push({
                        id: ev.id,
                        title: ev.title,
                        start: ev.start_at,
                        end: ev.end_at,
                        allDay: false,
                        backgroundColor: color,
                        borderColor: color,
                        extendedProps: { source: "calendar", row: ev },
                    });
                }
            });
        }
        if (showTasks) {
            tasks.forEach((task) => {
                const start = task.startDate || task.deadlineDate;
                const endInclusive = task.deadlineDate || task.startDate;
                if (!start || !endInclusive) return;
                out.push({
                    id: `task-${task.id}`,
                    title: `[Task] ${task.title}`,
                    start,
                    end: toExclusiveEnd(endInclusive.slice(0, 10)),
                    allDay: true,
                    backgroundColor: "#2E7D32",
                    borderColor: "#1B5E20",
                    editable: false,
                    extendedProps: { source: "task", taskId: task.id },
                });
            });
        }
        return out;
    }, [events, tasks, showEvents, showTasks]);

    const openNewFromSelect = (selectInfo) => {
        const allDay = selectInfo.allDay;
        if (allDay) {
            const start = selectInfo.startStr.slice(0, 10);
            const endStr = selectInfo.endStr.slice(0, 10);
            const inclusiveEnd = addDaysIso(endStr, -1);
            setForm({
                ...emptyForm,
                all_day: true,
                start_date: start,
                end_date_inclusive: inclusiveEnd,
            });
        } else {
            const s = selectInfo.start;
            const e = selectInfo.end;
            const pad = (n) => String(n).padStart(2, "0");
            const toLocal = (d) =>
                `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            setForm({
                ...emptyForm,
                all_day: false,
                start_dt: toLocal(s),
                end_dt: toLocal(e),
            });
        }
        setDialogOpen(true);
        selectInfo.view.calendar.unselect();
    };

    const openEditEvent = (row) => {
        if (row.all_day) {
            const start = row.start_at.slice(0, 10);
            const endEx = row.end_at.slice(0, 10);
            const inclusiveEnd = addDaysIso(endEx, -1);
            setForm({
                id: row.id,
                title: row.title,
                description: row.description || "",
                event_type: row.event_type || "meeting",
                all_day: true,
                start_date: start,
                end_date_inclusive: inclusiveEnd,
                start_dt: "",
                end_dt: "",
                location: row.location || "",
                customer_id: row.customer_id || "",
            });
        } else {
            const toLocalInput = (iso) => {
                if (!iso) return "";
                const d = new Date(iso);
                const pad = (n) => String(n).padStart(2, "0");
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
            };
            setForm({
                id: row.id,
                title: row.title,
                description: row.description || "",
                event_type: row.event_type || "meeting",
                all_day: false,
                start_date: "",
                end_date_inclusive: "",
                start_dt: toLocalInput(row.start_at),
                end_dt: toLocalInput(row.end_at),
                location: row.location || "",
                customer_id: row.customer_id || "",
            });
        }
        setDialogOpen(true);
    };

    const buildPayload = () => {
        if (form.all_day) {
            if (!form.start_date || !form.end_date_inclusive) {
                throw new Error("Start and end dates are required.");
            }
            const start_at = form.start_date;
            const end_at = toExclusiveEnd(form.end_date_inclusive);
            return {
                title: form.title.trim(),
                description: form.description.trim() || null,
                event_type: form.event_type,
                all_day: true,
                start_at,
                end_at,
                location: form.location.trim() || null,
                customer_id: form.customer_id || null,
                task_id: null,
            };
        }
        if (!form.start_dt || !form.end_dt) {
            throw new Error("Start and end date/time are required.");
        }
        const start_at = new Date(form.start_dt).toISOString();
        const end_at = new Date(form.end_dt).toISOString();
        return {
            title: form.title.trim(),
            description: form.description.trim() || null,
            event_type: form.event_type,
            all_day: false,
            start_at,
            end_at,
            location: form.location.trim() || null,
            customer_id: form.customer_id || null,
            task_id: null,
        };
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            setSnackbar({ open: true, message: "Title is required.", severity: "warning" });
            return;
        }
        try {
            const payload = buildPayload();
            const url = form.id ? `${API_URL}/calendar-events/${form.id}` : `${API_URL}/calendar-events`;
            const res = await apiFetch(url, {
                method: form.id ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Save failed");
            setSnackbar({
                open: true,
                message: form.id ? "Event updated." : "Event created.",
                severity: "success",
            });
            setDialogOpen(false);
            setForm(emptyForm);
            loadEvents();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const handleDelete = async () => {
        if (!form.id) return;
        try {
            const res = await apiFetch(`${API_URL}/calendar-events/${form.id}`, { method: "DELETE" });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Delete failed");
            setSnackbar({ open: true, message: "Event deleted.", severity: "success" });
            setDialogOpen(false);
            setForm(emptyForm);
            loadEvents();
        } catch (e) {
            setSnackbar({ open: true, message: e.message, severity: "error" });
        }
    };

    const persistEventDates = async (event) => {
        const row = event.extendedProps.row;
        if (!row) return;
        let start_at;
        let end_at;
        if (event.allDay) {
            start_at = event.startStr.slice(0, 10);
            end_at = event.endStr.slice(0, 10);
        } else {
            start_at = event.start.toISOString();
            end_at = event.end.toISOString();
        }
        const payload = {
            title: row.title,
            description: row.description,
            start_at,
            end_at,
            all_day: event.allDay,
            event_type: row.event_type,
            customer_id: row.customer_id,
            task_id: row.task_id,
            location: row.location,
        };
        const res = await apiFetch(`${API_URL}/calendar-events/${row.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || "Update failed");
        }
    };

    return (
        <Box sx={{ pb: 3 }}>
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 2.5 },
                    mb: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)}, ${alpha(theme.palette.background.paper, 0.96)} 58%)`,
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                display: "grid",
                                placeItems: "center",
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                color: "primary.contrastText",
                                bgcolor: "primary.main",
                                boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
                            }}
                        >
                            <CalendarMonthIcon />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 800, letterSpacing: "-0.02em" }}>
                                Business calendar
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Meetings, milestones, and task timelines in one view.
                            </Typography>
                        </Box>
                    </Stack>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => { setForm(emptyForm); setDialogOpen(true); }}
                        sx={{ alignSelf: { xs: "stretch", sm: "center" }, borderRadius: 2, px: 2.5 }}
                    >
                        New event
                    </Button>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1.5, md: 2 },
                    mb: 2,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2.5,
                    bgcolor: "background.paper",
                }}
            >
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.5}>
                    <FormGroup row sx={{ alignItems: "center", gap: { xs: 0, sm: 1 } }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showEvents}
                                    onChange={(e) => setShowEvents(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Scheduled events"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showTasks}
                                    onChange={(e) => setShowTasks(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Tasks (read-only)"
                        />
                    </FormGroup>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        {EVENT_TYPES.map((type) => (
                            <Chip
                                key={type.value}
                                size="small"
                                label={type.label}
                                sx={{
                                    borderRadius: 1.5,
                                    color: type.color,
                                    borderColor: alpha(type.color, 0.4),
                                    bgcolor: alpha(type.color, 0.1),
                                }}
                                variant="outlined"
                            />
                        ))}
                    </Stack>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1, sm: 1.5, md: 2 },
                    borderRadius: 3,
                    border: 1,
                    borderColor: "divider",
                    overflow: "hidden",
                    bgcolor: "background.paper",
                    boxShadow: theme.shadows[2],
                    "& .fc": {
                        color: "text.primary",
                    },
                    "& .fc-theme-standard td, & .fc-theme-standard th": {
                        borderColor: alpha(theme.palette.divider, 0.75),
                    },
                    "& .fc-scrollgrid": {
                        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        borderRadius: 2,
                        overflow: "hidden",
                    },
                    "& .fc-col-header-cell": {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        "& .fc-col-header-cell-cushion": {
                            color: "text.secondary",
                            fontWeight: 800,
                            fontSize: "0.72rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            padding: "12px 4px",
                        },
                    },
                    "& .fc-daygrid-day-number": {
                        color: "text.secondary",
                        fontWeight: 700,
                        padding: "8px 10px",
                    },
                    "& .fc-day-today": {
                        bgcolor: `${alpha(theme.palette.warning.main, 0.1)} !important`,
                        "& .fc-daygrid-day-number": {
                            color: "warning.main",
                        },
                    },
                    "& .fc-day-other .fc-daygrid-day-number": {
                        color: alpha(theme.palette.text.secondary, 0.42),
                    },
                    "& .fc-day-sat, & .fc-day-sun": {
                        bgcolor: alpha(theme.palette.text.primary, 0.018),
                    },
                    "& .fc-daygrid-day-frame": {
                        minHeight: { xs: 92, sm: 116 },
                    },
                    "& .fc-event": {
                        border: 0,
                        borderRadius: 1,
                        padding: "2px 5px",
                        margin: "2px 4px",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.16)}`,
                    },
                    "& .fc-daygrid-event-dot": {
                        borderColor: "currentColor",
                    },
                    "& .fc-toolbar": {
                        gap: 1,
                        flexWrap: "wrap",
                        marginBottom: "18px !important",
                    },
                    "& .fc-toolbar-title": {
                        color: "text.primary",
                        fontSize: { xs: "1.2rem", sm: "1.55rem" },
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                    },
                    "& .fc-button-group": {
                        gap: "4px",
                    },
                    "& .fc-button": {
                        backgroundColor: `${alpha(theme.palette.primary.main, 0.12)} !important`,
                        borderColor: `${alpha(theme.palette.primary.main, 0.2)} !important`,
                        borderRadius: "8px !important",
                        color: `${theme.palette.text.primary} !important`,
                        fontWeight: 700,
                        boxShadow: "none !important",
                        opacity: 1,
                        "&:hover": {
                            backgroundColor: `${alpha(theme.palette.primary.main, 0.22)} !important`,
                            borderColor: `${alpha(theme.palette.primary.main, 0.35)} !important`,
                        },
                    },
                    "& .fc-button-active": {
                        backgroundColor: `${theme.palette.primary.main} !important`,
                        borderColor: `${theme.palette.primary.main} !important`,
                        color: `${theme.palette.primary.contrastText} !important`,
                    },
                    "@media (max-width: 600px)": {
                        "& .fc-toolbar-chunk": {
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                        },
                        "& .fc-toolbar-title": {
                            fontSize: "1.25rem",
                        },
                    },
                }}
            >
                <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                    }}
                    height="clamp(560px, calc(100vh - 280px), 780px)"
                    selectable
                    selectMirror
                    dayMaxEvents={3}
                    moreLinkClick="popover"
                    nowIndicator
                    navLinks
                    editable
                    eventDurationEditable
                    eventStartEditable
                    events={calendarApiEvents}
                    select={openNewFromSelect}
                    eventClick={(info) => {
                        if (info.event.extendedProps.source === "task") {
                            setSnackbar({
                                open: true,
                                message: "Open Tasks to edit this item.",
                                severity: "info",
                            });
                            navigate("/tasks");
                            return;
                        }
                        openEditEvent(info.event.extendedProps.row);
                    }}
                    eventDrop={async (info) => {
                        if (info.event.extendedProps.source !== "calendar") {
                            info.revert();
                            return;
                        }
                        try {
                            await persistEventDates(info.event);
                            loadEvents();
                        } catch (e) {
                            info.revert();
                            setSnackbar({ open: true, message: e.message, severity: "error" });
                        }
                    }}
                    eventResize={async (info) => {
                        if (info.event.extendedProps.source !== "calendar") {
                            info.revert();
                            return;
                        }
                        try {
                            await persistEventDates(info.event);
                            loadEvents();
                        } catch (e) {
                            info.revert();
                            setSnackbar({ open: true, message: e.message, severity: "error" });
                        }
                    }}
                />
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{form.id ? "Edit event" : "New event"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Title"
                        fullWidth
                        margin="normal"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        margin="normal"
                        multiline
                        rows={2}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                    <TextField
                        label="Type"
                        fullWidth
                        margin="normal"
                        select
                        SelectProps={{ native: true }}
                        value={form.event_type}
                        onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                    >
                        {EVENT_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.all_day}
                                onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                            />
                        }
                        label="All day"
                        sx={{ mt: 1, display: "block" }}
                    />
                    {form.all_day ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Start"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.start_date}
                                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                            />
                            <TextField
                                label="End (inclusive)"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.end_date_inclusive}
                                onChange={(e) => setForm({ ...form, end_date_inclusive: e.target.value })}
                            />
                        </Stack>
                    ) : (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Start"
                                type="datetime-local"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.start_dt}
                                onChange={(e) => setForm({ ...form, start_dt: e.target.value })}
                            />
                            <TextField
                                label="End"
                                type="datetime-local"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.end_dt}
                                onChange={(e) => setForm({ ...form, end_dt: e.target.value })}
                            />
                        </Stack>
                    )}
                    <TextField
                        label="Location"
                        fullWidth
                        margin="normal"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                    />
                    <TextField
                        label="Customer (optional)"
                        fullWidth
                        margin="normal"
                        select
                        SelectProps={{ native: true }}
                        value={form.customer_id}
                        onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    >
                        <option value="">None</option>
                        {customers.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
                    <Box>
                        {form.id && (
                            <Button color="error" onClick={handleDelete}>
                                Delete
                            </Button>
                        )}
                    </Box>
                    <Box>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleSave}>
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Calendar;
